import type { BoardVisibility, FeedbackItem, StoppedFollowers, VisibilityResult } from '@/lib/apiClient';
import { firstLine } from '@/lib/feedbackMine';
import { SESSION_EXPIRED_MESSAGE, statusOf } from '@/lib/httpErrors';
import { plural } from '@/lib/plural';

/**
 * The rules behind the Feedback tab's board controls, kept out of the components so each is testable on its own.
 *
 * The API decides what is allowed (`decideVisibility` in `feedback-board.ts`). These mirror it only so the screen offers what will
 * work, and the answer for every id comes back anyway: a bulk action that quietly did less than it was asked is the wrong kind of
 * surprise on a privacy control, so what happened is always said (`describeVisibility`).
 */

/** The API takes at most this many ids in one visibility request (`VISIBILITY_BATCH_MAX`); `feedbackBoardContract.spec.ts` holds the two equal. */
export const SHOW_BATCH_MAX = 100;

/** What the Board column says. */
export const BOARD_LABEL: Record<BoardVisibility, string> = {
  unreviewed: 'Unreviewed',
  public: 'On the board',
  private: 'Private',
  hidden: 'Hidden',
};

/** ⚠️ `null` / absent is "UNKNOWN" (the API could not say: the board migration has not been run there), never "Unreviewed". */
export const boardLabel = (v: BoardVisibility | null | undefined): string => (v ? BOARD_LABEL[v] ?? 'Unknown' : 'Unknown');

type Visible = Pick<FeedbackItem, 'boardVisibility'>;

/**
 * An admin can show an item nobody has published yet, or one that was hidden. Never a private one, never one whose state is unknown, and ⚠️ NEVER ONE THAT WAS
 * MERGED into another request: a merge hides the item it closes on purpose, and showing it would put a closed duplicate, with every reply on it, back in
 * front of everyone as a request of its own. (The API refuses it too, for a list that was stale.)
 */
export const canShow = (i: Visible & Pick<FeedbackItem, 'duplicateOf'>): boolean =>
  (i.boardVisibility === 'unreviewed' || i.boardVisibility === 'hidden') && i.duplicateOf == null;

/**
 * An admin can hide what is on the board, and what nobody has reviewed yet: that is how an item that should never be shared is
 * marked as reviewed (`decideVisibility` in the API allows both, and the plan says why). Never a private one, never an unknown one.
 */
export const canHide = (i: Visible): boolean => i.boardVisibility === 'public' || i.boardVisibility === 'unreviewed';

/**
 * Whether "Stop telling followers" is offered: the item is HIDDEN (the API refuses any other) and somebody besides the sender follows it. A
 * count that could not be read (`null`) is not "somebody": nothing is offered on what is not known.
 *
 * ⚠️ NEVER FOR A MERGED ITEM. A merge hides the item it closes and COPIES its followers onto the other request, leaving them on this one on purpose
 * (the merge preview says so): it is where they read what became of their request. Removing them here would delete that explanation, and a closed
 * item has nothing more to tell them anyway.
 */
export const canStopTelling = (i: Visible & Pick<FeedbackItem, 'otherFollowerCount' | 'duplicateOf'>): boolean =>
  i.boardVisibility === 'hidden' && i.duplicateOf == null && i.otherFollowerCount != null && i.otherFollowerCount > 0;

/** The API's list holds the newest this many items (and any out of step with GitHub): `LISTED_FEEDBACK` in `feedback-list.ts`. */
export const LISTED_LIMIT = 500;

/** ⚠️ At or above the cap the list may be CUT, and "nothing to review" cannot be said of the items that are not in it. */
export const listMayBeCut = (listed: number): boolean => listed >= LISTED_LIMIT;

/** The board lists a CLOSED item for this many days after it closes (`BOARD_CLOSED_WINDOW_DAYS` in the API); an open one always. */
export const BOARD_CLOSED_WINDOW_DAYS = 90;

/** A closed item that closed too long ago for the board to list: showing it makes it public and nobody will see it. */
export const closedPastTheWindow = (i: Pick<FeedbackItem, 'status' | 'closedAt' | 'updatedAt'>, now: number = Date.now()): boolean => {
  if (i.status !== 'closed') return false;
  const closed = Date.parse(i.closedAt ?? i.updatedAt);
  return Number.isFinite(closed) && now - closed > BOARD_CLOSED_WINDOW_DAYS * 24 * 60 * 60 * 1000;
};

/** ONE definition of "nobody has reviewed it for the board yet": the To review list and its count, and what "select all" takes. */
export const isUnreviewed = (i: Visible): boolean => i.boardVisibility === 'unreviewed';

/** Items nobody has reviewed for the board yet: the "To review" filter. */
export const toReviewCount = (items: readonly Visible[]): number => items.filter(isUnreviewed).length;

/**
 * "Select all": the UNREVIEWED items in `rows`, the first `max` of them, in the order the list shows them.
 *
 * ⚠️ NOT THE HIDDEN ONES, though an admin can show those. A hidden item was kept off the board on purpose (that is how "Not for the
 * board" marks an item as reviewed), and "select all" from the Open or All list would put every one of them back in front of the next
 * confirm. A hidden item is shown by ticking it by itself, and the preview says it was hidden.
 */
export const selectableIds = (rows: readonly (Visible & Pick<FeedbackItem, 'id'>)[], max = SHOW_BATCH_MAX): number[] =>
  rows.filter(isUnreviewed).slice(0, max).map((i) => i.id);

/**
 * The items ticked to be shown, and what each READ AS when it was ticked.
 *
 * ⚠️ A TICK HOLDS ONLY WHILE THE ITEM STILL READS THE SAME. An item ticked as unreviewed and then marked "not for the board" (or shown,
 * or hidden by another admin) is a different item to confirm, and `canShow` alone would keep it ticked: it is still showable once
 * hidden, so one confirm would publish what the admin had just decided to keep off the board.
 */
export type Ticks = ReadonlyMap<number, BoardVisibility | null | undefined>;

type Tickable = Visible & Pick<FeedbackItem, 'id'>;

export const isTicked = (ticks: Ticks, i: Tickable): boolean => ticks.has(i.id) && ticks.get(i.id) === i.boardVisibility;

/** A copy of `ticks` with `i` ticked as it reads now, or unticked if it already is. A stale tick (it reads differently now) is ticked afresh. */
export const toggleTick = (ticks: Ticks, i: Tickable): Map<number, BoardVisibility | null | undefined> => {
  const next = new Map(ticks);
  if (isTicked(ticks, i)) next.delete(i.id); else next.set(i.id, i.boardVisibility);
  return next;
};

/** "Select all": every item `selectableIds` takes, each ticked as it reads now. */
export const tickAll = (rows: readonly Tickable[], max = SHOW_BATCH_MAX): Map<number, BoardVisibility | null | undefined> => {
  const ids = new Set(selectableIds(rows, max));
  return new Map(rows.filter((i) => ids.has(i.id)).map((i) => [i.id, i.boardVisibility]));
};

/** A copy of `set` with `id` added, or removed if it was there: a tick in a checkbox list, which state must not change in place. */
export const toggleIn = <T>(set: ReadonlySet<T>, id: T): Set<T> => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
};

/**
 * ⚠️ Whether the conversation on an item was read for this list. When it could not be (the replies-and-followers migration has not been
 * run on that database) the API sends `followerCount: null` WITH `replies: []`, so an empty list of replies here does not mean "no
 * replies", and an item whose replies cannot be seen must not be shown: showing it publishes them.
 */
export const repliesLoaded = (i: Pick<FeedbackItem, 'followerCount'>): boolean => i.followerCount != null;

export type ReplyKind = 'reply' | 'note';

/**
 * The sentence an admin needs under the reply box (or the closing note) about the BOARD, or `null` when the board changes nothing.
 * Replies are stored with the item, so what is written now is read by everyone signed in the moment the item is public, and an item
 * nobody has shown yet, or one that was hidden, publishes every reply it already has when it is shown. A private item never is.
 */
export function boardReplyNote(v: BoardVisibility | null | undefined, kind: ReplyKind): string | null {
  const what = kind === 'reply' ? 'reply' : 'note';
  if (v === 'public') return `This item is on the board, so everyone signed in can read this ${what} there, with your name.`;
  if (v === 'unreviewed' || v === 'hidden') return `If this item is shown on the board, this ${what} is public with it, with your name.`;
  return null;
}

/**
 * How many replies went public that the admin did not see in the preview: `publishedReplies` counts every reply on the item at the
 * moment it was shown, and `previewed` is how many the preview listed for each id. A reply added in between (by another admin) is
 * public all the same, so the answer says so.
 */
export function lateReplies(results: readonly VisibilityResult[], previewed: ReadonlyMap<number, number>): number {
  return results.reduce((n, r) => (r.outcome === 'changed' && r.publishedReplies != null ? n + Math.max(0, r.publishedReplies - (previewed.get(r.id) ?? 0)) : n), 0);
}

const sum = (xs: (number | undefined)[]) => xs.reduce<number>((a, b) => a + (b ?? 0), 0);

export type VisibilityNote = { tone: 'ok' | 'warn' | 'error'; text: string };

/**
 * What stopping an item's followers did, in a sentence. ⚠️ Silence after an irreversible action reads as success, so nobody removed (somebody else
 * got there first, or they had all left) is said, not left to be guessed.
 *
 * ⚠️ `removed` COUNTS THE PEOPLE WHO COULD STILL BE TOLD, `total` EVERY FOLLOW DELETED. They differ by the accounts that have since been
 * removed, so a result of `removed: 0, total: 2` is NOT "nobody was following it": two follows were deleted, and the sentence says so.
 */
export function describeStopped(result: Pick<StoppedFollowers, 'removed' | 'total'>): VisibilityNote {
  const { removed, total } = result;
  if (removed > 0) {
    return { tone: 'ok', text: `${plural(removed, 'person is', 'people are')} no longer told about it, and it has left their My feedback. This cannot be undone.` };
  }
  if (total > 0) {
    const accounts = plural(total, 'account', 'accounts');
    return { tone: 'ok', text: `The ${total === 1 ? 'follow' : 'follows'} of ${accounts} that ${total === 1 ? 'has' : 'have'} since been removed ${total === 1 ? 'was' : 'were'} deleted. Nobody who could still be told was following it. This cannot be undone.` };
  }
  return { tone: 'warn', text: 'Nobody but the person who sent it was following it any more, so nobody was removed.' };
}

/**
 * The accessible name of the Board column's "Stop telling followers…" on one row: it begins with the words on the button, then says WHICH item
 * and how many people. A table of hidden items would otherwise be a column of identical, unnamed buttons for a screen reader, on an action that
 * cannot be undone, and the count was only in a hover tooltip.
 */
export const stoppedLabel = (i: Pick<FeedbackItem, 'message' | 'otherFollowerCount'>): string =>
  `Stop telling followers…: “${firstLine(i.message, 50)}”, ${plural(i.otherFollowerCount ?? 0, 'person follows', 'people follow')} it besides the sender`;

/**
 * The statuses that are the API refusing a request BEFORE anything ran: a bad id, no sign-in, no permission, not found, a conflict, a refusal to
 * act, and 424 (the record of who would be removed cannot be written, so the removal rolled back whole and nothing was removed).
 */
const REFUSED_BEFORE_RUNNING: ReadonlySet<number> = new Set([400, 401, 403, 404, 409, 422, 424, 429]);

/**
 * Whether a request that failed may nonetheless have been applied. Those statuses are the API's OWN refusals, made before anything ran, so a
 * hedge about "if any of it was applied" would contradict the sentence it follows. Everything else says nothing about whether the write
 * happened: no status at all (a dropped connection), a 5xx, and a 408 or any other 4xx, which may come from something between the browser and
 * the API after the request was passed on. On an action that cannot be undone the hedge is dropped only when it is certain. (Not
 * `isDefiniteRejection`, which answers a different question, whether it is safe to SEND AGAIN, and so leaves out a 409 on purpose.)
 */
export const mayHaveBeenApplied = (error: unknown): boolean => {
  // The sign-in that could not be renewed is raised BEFORE the request is sent, and has no status: nothing ran.
  if ((error as { message?: unknown } | null | undefined)?.message === SESSION_EXPIRED_MESSAGE) return false;
  const status = statusOf(error);
  return status === undefined || !REFUSED_BEFORE_RUNNING.has(status);
};

/** The tooltip on that button: the count in words, which the accessible name carries too. */
export const stoppedTitle = (i: Pick<FeedbackItem, 'otherFollowerCount'>): string =>
  `${plural(i.otherFollowerCount ?? 0, 'person follows', 'people follow')} it besides the sender, and still hear about it`;

/** The heading over that answer: derived from it, never fixed. */
export const stoppedHeading = (result: Pick<StoppedFollowers, 'removed' | 'total'>): string =>
  (result.removed > 0 || result.total > 0 ? 'Followers stopped' : 'Nobody was removed');

/**
 * The heading over what a show or a hide did. ⚠️ DERIVED FROM THE ANSWER, never fixed: a heading that says "Taken off the board" over a
 * body that says the write failed is the one a skimming admin believes, and on a privacy control it is the wrong one.
 */
export function visibilityHeading(results: readonly VisibilityResult[], wanted: 'public' | 'hidden', fromQueue = false): string {
  const changed = results.filter((r) => r.outcome === 'changed').length;
  if (changed === 0) return 'Nothing was changed';
  if (changed < results.length) return 'Some items were not changed';
  return wanted === 'public' ? 'Shown on the board' : fromQueue ? 'Kept off the board' : 'Taken off the board';
}

/**
 * What a show or a hide did, in plain sentences, for EVERY id it was asked about.
 *
 * ⚠️ `removeFollowers` is what the admin ASKED for, and the sentence says what HAPPENED. Followers are removed only from the items THIS
 * request hid, so an item that was already hidden, refused or changed by somebody else says nobody was removed from it: the box said
 * "cannot be undone", and silence after it would read as success.
 */
export function describeVisibility(
  results: readonly VisibilityResult[],
  wanted: 'public' | 'hidden',
  asked: { removeFollowers?: boolean } = {},
): VisibilityNote {
  const changed = results.filter((r) => r.outcome === 'changed');
  const unchanged = results.filter((r) => r.outcome === 'unchanged');
  const refusedPrivate = results.filter((r) => r.outcome === 'refused' && r.reason === 'private');
  const refusedMerged = results.filter((r) => r.outcome === 'refused' && r.reason === 'merged');
  const refusedOther = results.filter((r) => r.outcome === 'refused' && r.reason !== 'private' && r.reason !== 'merged');
  const conflicts = results.filter((r) => r.outcome === 'conflict');
  const gone = results.filter((r) => r.outcome === 'not_found');
  const errors = results.filter((r) => r.outcome === 'error');
  const them = (n: number) => (n === 1 ? 'it' : 'them');

  const say: string[] = [];
  if (changed.length) {
    say.push(`${wanted === 'public' ? 'Showed' : 'Hid'} ${plural(changed.length, 'item', 'items')}${wanted === 'public' ? ' on the board' : ''}.`);
    if (wanted === 'public') {
      const replies = sum(changed.map((r) => r.publishedReplies));
      if (replies > 0) say.push(`${plural(replies, 'reply is', 'replies are')} now public with ${them(changed.length)}.`);
      if (changed.some((r) => r.publishedReplies == null)) say.push('How many replies went public could not be counted.');
    } else if (asked.removeFollowers) {
      const removed = sum(changed.map((r) => r.removedFollowers));
      const uncounted = changed.filter((r) => r.removedFollowers == null).length;
      if (removed > 0) say.push(`${plural(removed, 'person is', 'people are')} no longer told about ${them(changed.length)}.`);
      // ⚠️ Silence after the irreversible tick reads as success. Nobody removed because nobody else followed is said, not left to be guessed.
      if (uncounted) say.push(`How many people were removed from ${plural(uncounted, 'item', 'items')} could not be counted.`);
      else if (removed === 0) say.push(`Nobody else followed ${them(changed.length)}, so nobody was removed.`);
    } else {
      const keep = sum(changed.map((r) => r.otherFollowers));
      if (keep > 0) say.push(`${plural(keep, 'other person', 'other people')} still ${keep === 1 ? 'follows' : 'follow'} ${them(changed.length)} and ${keep === 1 ? 'keeps' : 'keep'} ${them(changed.length)} under My feedback.`);
      // ⚠️ This sentence is the only warning that Hide does not stop the emails, so when the count failed (the API leaves it off) say
      // that, rather than say nothing and read as "nobody follows it".
      if (changed.some((r) => r.otherFollowers == null)) say.push(`How many people still follow ${them(changed.length)} could not be counted: anybody who does keeps ${them(changed.length)} under My feedback.`);
    }
  }
  if (unchanged.length) say.push(`${plural(unchanged.length, 'item was', 'items were')} already ${wanted === 'public' ? 'on the board' : 'off it'}.`);
  if (refusedPrivate.length) say.push(`${plural(refusedPrivate.length, 'item was', 'items were')} not changed: the sender asked to keep ${them(refusedPrivate.length)} private.`);
  if (refusedMerged.length) say.push(`${plural(refusedMerged.length, 'item was', 'items were')} not changed: ${refusedMerged.length === 1 ? 'it was' : 'they were'} combined into another request, so ${refusedMerged.length === 1 ? 'it' : 'they'} cannot be shown.`);
  if (refusedOther.length) say.push(`${plural(refusedOther.length, 'item was', 'items were')} not changed: ${refusedOther.length === 1 ? 'its' : 'their'} state is one this screen does not know.`);
  if (conflicts.length) say.push(`${plural(conflicts.length, 'item was', 'items were')} changed by someone else in the meantime. Look at the list.`);
  if (gone.length) say.push(`${plural(gone.length, 'item no longer exists', 'items no longer exist')}.`);
  // ⚠️ The opt-in is only ever done to an item this request hid, so say plainly where it was not.
  const notHidden = unchanged.length + refusedPrivate.length + refusedOther.length + conflicts.length + gone.length;
  const removalMissed = wanted === 'hidden' && !!asked.removeFollowers && notHidden > 0;
  if (removalMissed) say.push(`Nobody was removed from ${plural(notHidden, 'item', 'items')} (followers are removed only from items hidden by this request).`);
  if (errors.length) say.push(`${plural(errors.length, 'item', 'items')} could not be changed, and it may or may not have been. Look at the list to see where ${errors.length === 1 ? 'it stands' : 'they stand'}.`);
  if (!say.length) say.push('Nothing was changed.');

  const tone: VisibilityNote['tone'] = errors.length ? 'error' : refusedPrivate.length || refusedMerged.length || refusedOther.length || conflicts.length || gone.length || removalMissed ? 'warn' : 'ok';
  return { tone, text: say.join(' ') };
}
